import { HTMLAttributes } from 'react';
import withStyles from '@mui/styles/withStyles';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput';
import type { Theme } from '@mui/material/styles';

type genericStyle = {
  disabled?: boolean;
  error?: boolean;
};

const InputRoot = withStyles((theme: Theme) => {
  const getBorderColor = (props: genericStyle) => {
    if (props?.disabled) {
      return theme.palette.darkGrey[15];
    }

    if (props?.error) {
      return theme.palette.error.main;
    }

    return theme.palette.darkGrey[50];
  };

  return {
    root: {
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,

      '&:hover $notchedOutline': {
        borderColor: (props: genericStyle) => (props.error ? theme.palette.error.main : theme.palette.common.black),
        borderWidth: 1,
      },

      '@media (hover: none)': {
        '&:hover $notchedOutline': {
          borderColor: (props) => getBorderColor(props),
          borderWidth: 1,
        },
      },

      'label + &': {
        marginTop: theme.spacing(1),
      },
    },

    notchedOutline: {
      borderColor: (props) => getBorderColor(props),
      borderWidth: 1,
    },

    focused: {
      borderWidth: 1,
    },

    disabled: {
      color: theme.palette.darkGrey[15],
    },

    error: {
      color: theme.palette.error.main,
    },

    input: {
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      fontSize: theme.typography.pxToRem(14),
      padding: theme.typography.pxToRem(12),
      height: 42,
      boxSizing: 'border-box',
    },

    inputAdornedStart: {
      paddingLeft: 16,
    },
  };
})(OutlinedInput);

const InputLabelRoot = withStyles((theme: Theme) => ({
  root: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.common.black,
    position: 'relative',
    lineHeight: 1,
  },

  shrink: {
    transform: 'translate(0, 1.5px) scale(1)',
    transformOrigin: 'top left',
  },
}))(InputLabel);

const FormHelperTextRoot = withStyles((theme: Theme) => ({
  root: {
    color: theme.palette.darkGrey[50],
    fontSize: 12,
  },
}))(FormHelperText);

interface Props extends OutlinedInputProps {
  label?: string | null;
  helperText?: string;
  errorMsg?: string;
  autInputLabelProps: InputLabelProps;
  autInputProps: OutlinedInputProps;
  rootProps: HTMLAttributes<HTMLDivElement>;
}

function InputTextField({
  id,
  error,
  fullWidth,
  label = null,
  helperText,
  errorMsg,
  disabled,
  required,
  autInputLabelProps,
  autInputProps,
  rootProps,
  ...others
}: Props) {
  return (
    <div {...rootProps}>
      <FormControl error={error} fullWidth={fullWidth} required={required} variant="standard">
        {label && (
          <InputLabelRoot htmlFor={id} shrink {...autInputLabelProps}>
            {label}
          </InputLabelRoot>
        )}

        <InputRoot disabled={disabled} error={error} id={id} required={required} {...autInputProps} {...others} />

        <FormHelperTextRoot id={`${id}HelperText`}>{errorMsg || helperText}</FormHelperTextRoot>
      </FormControl>
    </div>
  );
}

export default InputTextField;
